"use client";

import type { TemplateType } from "@/features/calendar/types/templates";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Check, Loader2, X } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../dialog.tsx";

import { TemplateCustomSlotTab } from "./TemplateCustomSlotTab.tsx";
import { TemplateFillTimeRangeTab } from "./TemplateFillTimeRangeTab.tsx";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { LessonHost } from "@/features/calendar/types/create-slot.ts";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions.tsx";
import { Badge } from "@/components/ui/badge";
import {
  generateTimeframePreviewSlots,
  generateWeeklyPreviewSlots,
  getHostingMethodNameAndIcon,
  type PreviewSlot,
  type WeeklySlotData,
} from "@/features/calendar/utils/slot-creation";
import { InformationTooltip } from "@/components/InformationTooltip.tsx";

interface TemplateSlotDialogProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateType: TemplateType;
  onSuccess?: () => void;
  initialHour?: number;
  initialDay?: number;
}

export function TemplateSlotDialog({
  isOpen,
  onClose,
  templateId,
  templateType,
  onSuccess,
  initialHour,
  initialDay,
}: TemplateSlotDialogProps) {
  const { t } = useTranslation(["calendar", "forms", "common"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hostingOptions = useHostingOptions();

  // State for multiple slot creation
  const [createMultiple, setCreateMultiple] = useState(false);
  const [timeframeEndTime, setTimeframeEndTime] = useState<string>("10:00");
  const [breakMinutes, setBreakMinutes] = useState<number | undefined>(0);
  const [numberOfSlots, setNumberOfSlots] = useState<number | undefined>(1);
  const [maxSlots, setMaxSlots] = useState(5);
  const [slotDurationMinutes, setSlotDurationMinutes] = useState<number | undefined>(60);
  const [activeTab, setActiveTab] = useState<"custom" | "range">("custom");

  // State for two-step process
  const [currentStep, setCurrentStep] = useState<"creation" | "preview">("creation");
  const [previewSlots, setPreviewSlots] = useState<PreviewSlot[]>([]);
  const [hasSuccessfulCreation, setHasSuccessfulCreation] = useState(false);

  const createTemplateSlotSchema = useMemo(() => {
    return z
      .object({
        start_time: z
          .string()
          .regex(/^\d{2}:\d{2}$/, { message: t("forms:templates.validation.startTimeFormat") }),
        end_time: z
          .string()
          .regex(/^\d{2}:\d{2}$/, { message: t("forms:templates.validation.endTimeFormat") }),
        day_of_week: z.number().min(1).max(7).optional(),
        lesson_host: z.string(),
        lesson_type: z.string().default("individual"),
        communication_details: z.string().optional(),
      })
      .refine(
        (data) => {
          if (activeTab === "range") return true;
          const [startHour, startMinute] = data.start_time.split(":").map(Number);
          const [endHour, endMinute] = data.end_time.split(":").map(Number);

          if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
            return true;
          }
          const startMinutes = startHour * 60 + startMinute;
          const endMinutes = endHour * 60 + endMinute;

          return endMinutes - startMinutes >= 10;
        },
        {
          message: t("forms:templates.validation.durationMinimum"),
          path: ["end_time"],
        },
      )
      .refine(
        (data) => {
          if (templateType === "weekly" || activeTab === "range") {
            return true;
          }
          const [startHour, startMinute] = data.start_time.split(":").map(Number);
          const [endHour, endMinute] = data.end_time.split(":").map(Number);
          const startMinutes = startHour * 60 + startMinute;
          const endMinutes = endHour * 60 + endMinute;

          return endMinutes - startMinutes <= 360;
        },
        {
          message: t("forms:templates.validation.durationMaximum"),
          path: ["end_time"],
        },
      )
      .refine(
        (data) => {
          return (
            data.lesson_host !== "other_way" ||
            (data.communication_details && data.communication_details.trim() !== "")
          );
        },
        {
          message: t("forms:templates.validation.communicationDetailsRequired"),
          path: ["communication_details"],
        },
      );
  }, [t, templateType, activeTab]);

  const getInitialStartTime = () => {
    if (initialHour !== undefined) {
      return format(new Date().setHours(initialHour, 0, 0), "HH:mm");
    }

    return "09:00";
  };

  const getInitialEndTime = () => {
    if (initialHour !== undefined) {
      return format(new Date().setHours(initialHour + 1, 0, 0), "HH:mm");
    }

    return "10:00";
  };

  const form = useForm({
    resolver: zodResolver(createTemplateSlotSchema),
    mode: "onSubmit",
    defaultValues: {
      start_time: getInitialStartTime(),
      end_time: getInitialEndTime(),
      day_of_week: templateType === "weekly" ? initialDay || 1 : undefined,
      lesson_host: LessonHost.Ximly,
      lesson_type: "individual",
      communication_details: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        start_time: getInitialStartTime(),
        end_time: getInitialEndTime(),
        day_of_week: templateType === "weekly" ? initialDay || 1 : undefined,
        lesson_host: LessonHost.Ximly,
        lesson_type: "individual",
        communication_details: "",
      });
      setCurrentStep("creation");
      setPreviewSlots([]);
      setCreateMultiple(false);
      setTimeframeEndTime(getInitialEndTime());
      setNumberOfSlots(1);
      setBreakMinutes(0);
      setSlotDurationMinutes(60);
      setActiveTab("custom");
      setHasSuccessfulCreation(false);
    }
  }, [isOpen, initialHour, initialDay, form, templateType]);

  useEffect(() => {
    const endTime = form.watch("end_time");

    if (endTime && activeTab === "custom") {
      setTimeframeEndTime(endTime);
    }
  }, [form.watch("end_time"), activeTab]);

  useEffect(() => {
    if (form.watch("start_time") && form.watch("end_time")) {
      if (templateType === "weekly") {
        setMaxSlots(10);

        return;
      }
      setMaxSlots(10);
    }
  }, [form.watch("start_time"), form.watch("end_time"), templateType]);

  const handleProceedToPreview = async (data: WeeklySlotData) => {
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    if (activeTab === "range") {
      if (slotDurationMinutes === undefined || slotDurationMinutes < 10) {
        toast.error(t("calendar:templates.minSlotDuration"));

        return;
      }
    }

    let slots: PreviewSlot[];

    if (activeTab === "range") {
      slots = generateTimeframePreviewSlots(
        data,
        timeframeEndTime,
        breakMinutes,
        slotDurationMinutes,
      );
    } else {
      slots = generateWeeklyPreviewSlots(data, createMultiple, numberOfSlots, breakMinutes);
    }

    if (slots.length === 0) {
      toast.error(t("calendar:templates.noSlotsGenerated"));

      return;
    }

    // For single slot, create directly without preview
    if (activeTab === "custom" && slots.length === 1) {
      await handleCreateSlotsDirectly(slots);

      return;
    }

    setPreviewSlots(slots);
    setCurrentStep("preview");
  };

  const handleCreateSlotsDirectly = async (slots: PreviewSlot[]) => {
    setIsSubmitting(true);
    const updatedSlots = [...slots];

    for (let i = 0; i < updatedSlots.length; i++) {
      const slot = updatedSlots[i];

      try {
        await axiosApiInstance.post(`${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}/slots`, {
          start_time: slot.start_time,
          end_time: slot.end_time,
          day_of_week: slot.day_of_week,
          lesson_host: slot.lesson_host,
          communication_details: slot.communication_details,
        });
        updatedSlots[i] = { ...slot, status: "success" };
      } catch (error: any) {
        switch (error.response?.data?.code) {
          case "database_overlap":
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("forms:createSlotModal.toast.databaseOverlap"),
              errorType: "database_overlap",
            };
            break;
          default:
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("common:error"),
            };
            break;
        }
      }
    }

    const allSuccess = updatedSlots.every((slot) => slot.status === "success");

    if (allSuccess) {
      toast.success(t("calendar:templates.slotCreated"), {
        description: t("calendar:templates.slotCreatedDescription"),
      });
      setHasSuccessfulCreation(true);
      setTimeout(() => {
        setIsSubmitting(false);
        onClose();
        onSuccess?.();
      }, 100);
    } else {
      // For single slot, show the specific error message
      if (updatedSlots.length === 1) {
        const failedSlot = updatedSlots[0];

        toast.error(t("calendar:templates.slotCreationFailed"), {
          description: failedSlot.error || t("calendar:templates.allSlotsFailed"),
        });
      } else {
        toast.error(t("calendar:slotCreationFailed"), {
          description: t("calendar:templates.allSlotsFailed"),
        });
      }
      setIsSubmitting(false);
    }
  };

  const handleBackToCreation = () => {
    setCurrentStep("creation");
  };

  const getDayName = (day?: number) => {
    if (!day) return "";
    switch (day) {
      case 1:
        return t("calendar:days.monday");
      case 2:
        return t("calendar:days.tuesday");
      case 3:
        return t("calendar:days.wednesday");
      case 4:
        return t("calendar:days.thursday");
      case 5:
        return t("calendar:days.friday");
      case 6:
        return t("calendar:days.saturday");
      case 7:
        return t("calendar:days.sunday");
      default:
        return "";
    }
  };

  const handleCreateSlots = async () => {
    setIsSubmitting(true);
    const updatedSlots = [...previewSlots];

    for (let i = 0; i < updatedSlots.length; i++) {
      const slot = updatedSlots[i];

      try {
        await axiosApiInstance.post(`${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}/slots`, {
          start_time: slot.start_time,
          end_time: slot.end_time,
          day_of_week: slot.day_of_week,
          lesson_host: slot.lesson_host,
          communication_details: slot.communication_details,
        });
        updatedSlots[i] = { ...slot, status: "success" };
        setPreviewSlots([...updatedSlots]);
      } catch (error: any) {
        switch (error.response?.data?.code) {
          case "database_overlap":
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("forms:createSlotModal.toast.databaseOverlap"),
              errorType: "database_overlap",
            };
            setPreviewSlots([...updatedSlots]);
            break;
          default:
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("common:error"),
            };
            setPreviewSlots([...updatedSlots]);
            break;
        }
      }
    }

    const allSuccess = updatedSlots.every((slot) => slot.status === "success");
    const successCount = updatedSlots.filter((slot) => slot.status === "success").length;

    if (allSuccess) {
      toast.success(
        updatedSlots.length > 1
          ? t("calendar:templates.multipleSlotsCreated", { count: updatedSlots.length })
          : t("calendar:templates.slotCreated"),
        {
          description:
            updatedSlots.length > 1
              ? t("calendar:templates.multipleSlotsCreatedDescription", {
                  count: updatedSlots.length,
                })
              : t("calendar:templates.slotCreatedDescription"),
        },
      );
      setHasSuccessfulCreation(true);
      setTimeout(() => {
        setIsSubmitting(false);
        onClose();
        onSuccess?.();
      }, 1500);
    } else if (successCount > 0) {
      toast.success(t("calendar:templates.someSlotCreated", { count: successCount }), {
        description: t("calendar:templates.someSlotCreatedDescription"),
      });
      setHasSuccessfulCreation(true);
      setIsSubmitting(false);
    } else {
      toast.error(t("calendar:slotCreationFailed"), {
        description: t("calendar:templates.allSlotsFailed"),
      });
      setIsSubmitting(false);
    }
  };

  const hasApiErrors = () => {
    return previewSlots.some((slot) => slot.status === "error");
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (hasSuccessfulCreation) {
            onSuccess?.();
          }
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="bg-background absolute! left-0 flex h-dvh max-w-full flex-col gap-0 rounded-e-none! border-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:left-[35%] md:max-w-[65%] xl:left-[60%] xl:max-w-[40vw]"
      >
        <motion.div
          animate={{ x: 0, opacity: 1 }}
          className="flex h-full flex-col"
          exit={{ x: 100, opacity: 0 }}
          initial={{ x: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <DialogHeader className="border-border border-b bg-transparent px-6 py-4">
            <DialogTitle className="text-accent-foreground text-xl font-semibold">
              {currentStep === "creation"
                ? t("calendar:templates.addNewSlot")
                : t("calendar:templates.previewSlots")}
            </DialogTitle>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {currentStep === "creation" ? (
              <motion.div
                key="creation-step"
                animate={{ opacity: 1 }}
                className="flex flex-grow flex-col"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Form {...form}>
                  <div className="flex h-full flex-col">
                    <ScrollArea className="h-[calc(100dvh_-_140px)]">
                      <div className="mx-6 mt-4">
                        <Card className="bg-muted/20 relative rounded-2xl border-0 p-2 shadow-2xl backdrop-blur-xl">
                          <div className={"absolute inset-0 overflow-hidden rounded-2xl"}>
                            <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 blur-2xl" />
                            <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-gradient-to-tr from-green-500/15 to-emerald-600/15 blur-xl" />
                          </div>
                          <div className="relative z-10 grid grid-cols-2 gap-2">
                            <button
                              className={`relative overflow-hidden rounded-xl p-4 transition-all duration-200 ${
                                activeTab === "custom"
                                  ? "bg-background shadow-lg"
                                  : "hover:bg-background/50"
                              }`}
                              onClick={() => {
                                setActiveTab("custom");
                                setCreateMultiple(false);
                                setNumberOfSlots(1);
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`flex size-10 items-center justify-center rounded-lg transition-colors ${
                                    activeTab === "custom" ? "bg-muted" : "bg-muted/50"
                                  }`}
                                >
                                  <Icon
                                    className={
                                      activeTab === "custom"
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground"
                                    }
                                    height={"1.25rem"}
                                    icon="tabler:calendar-plus"
                                    width={"1.25rem"}
                                  />
                                </div>
                                <div className="text-left">
                                  <div
                                    className={`text-sm font-medium transition-colors ${
                                      activeTab === "custom"
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground"
                                    }`}
                                  >
                                    {t("calendar:templates.singleSlot")}
                                  </div>
                                  <div className="text-muted-foreground mt-0.5 text-xs">
                                    {t("calendar:templates.createSingleSlot")}
                                  </div>
                                </div>
                              </div>
                            </button>
                            <button
                              className={`relative overflow-hidden rounded-xl p-4 transition-all duration-200 ${
                                activeTab === "range"
                                  ? "bg-background shadow-lg"
                                  : "hover:bg-background/50"
                              }`}
                              onClick={() => setActiveTab("range")}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`flex size-10 items-center justify-center rounded-lg transition-colors ${
                                    activeTab === "range" ? "bg-muted" : "bg-muted/50"
                                  }`}
                                >
                                  <Icon
                                    className={
                                      activeTab === "range"
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground"
                                    }
                                    height={"1.25rem"}
                                    icon="tabler:calendar-time"
                                    width={"1.25rem"}
                                  />
                                </div>
                                <div className="text-left">
                                  <div
                                    className={`text-sm font-medium transition-colors ${
                                      activeTab === "range"
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground"
                                    }`}
                                  >
                                    {t("calendar:templates.fillTimeframe")}
                                  </div>
                                  <div className="text-muted-foreground mt-0.5 text-xs">
                                    {t("calendar:templates.fillTimeframeDescription")}
                                  </div>
                                </div>
                              </div>
                            </button>
                          </div>
                        </Card>
                      </div>

                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeTab}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -15 }}
                          initial={{ opacity: 0, y: 15 }}
                          transition={{ duration: 0.2, ease: "easeInOut" }}
                        >
                          {activeTab === "custom" ? (
                            <TemplateCustomSlotTab
                              breakMinutes={breakMinutes}
                              createMultiple={createMultiple}
                              form={form}
                              hostingOptions={hostingOptions}
                              maxSlots={maxSlots}
                              numberOfSlots={numberOfSlots}
                              setBreakMinutes={setBreakMinutes}
                              setCreateMultiple={setCreateMultiple}
                              setNumberOfSlots={setNumberOfSlots}
                              templateType={templateType}
                            />
                          ) : (
                            <TemplateFillTimeRangeTab
                              breakMinutes={breakMinutes}
                              form={form}
                              hostingOptions={hostingOptions}
                              setBreakMinutes={setBreakMinutes}
                              setSlotDurationMinutes={setSlotDurationMinutes}
                              setTimeframeEndTime={setTimeframeEndTime}
                              slotDurationMinutes={slotDurationMinutes}
                              t={t}
                              templateType={templateType}
                              timeframeEndTime={timeframeEndTime}
                            />
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </ScrollArea>

                    <div className="border-border bg-background p-6 pt-0">
                      <div className="mt-6 flex justify-end">
                        <Button
                          disabled={isSubmitting}
                          variant={"foreground"}
                          onClick={form.handleSubmit(handleProceedToPreview)}
                        >
                          <div className="flex items-center gap-1.5">
                            <Icon height="18" icon="tabler:eye" width="18" />
                            <span>
                              {activeTab === "custom" && numberOfSlots === 1
                                ? t("calendar:templates.createSlot")
                                : t("calendar:templates.previewSlots")}
                            </span>
                          </div>
                        </Button>
                      </div>
                    </div>
                  </div>
                </Form>
              </motion.div>
            ) : (
              <motion.div
                key="preview-step"
                animate={{ opacity: 1 }}
                className="flex flex-grow flex-col"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ScrollArea className="h-[calc(100dvh_-_130px)]">
                  <div className="space-y-6 p-6">
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      initial={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="bg-muted/50 mb-6 rounded-md p-4">
                        <div className="flex items-center">
                          <Icon
                            className="text-muted-foreground mr-2"
                            height={"1.25rem"}
                            icon="tabler:info-circle"
                            width={"1.25rem"}
                          />
                          <p className="text-muted-foreground">
                            {t("calendar:templates.previewDescription")}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {previewSlots.map((slot, index) => {
                          const { icon, name } = getHostingMethodNameAndIcon(
                            slot.lesson_host,
                            hostingOptions,
                          );

                          return (
                            <motion.div
                              key={`preview-slot-${slot.id}`}
                              animate={{ opacity: 1, y: 0 }}
                              initial={{ opacity: 0, y: 20 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                            >
                              <Card className="bg-muted/20 relative overflow-hidden rounded-2xl border-0 shadow-xl backdrop-blur-xl">
                                <div className="absolute -top-12 -right-12 h-24 w-24 rounded-full bg-gradient-to-br from-emerald-500/15 to-teal-600/15 blur-2xl" />
                                <CardContent className="z-50 p-4">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="bg-muted flex size-10 items-center justify-center rounded-lg">
                                        <Icon
                                          className="text-accent-foreground"
                                          height={"1.25rem"}
                                          icon="tabler:calendar-stats"
                                          width={"1.25rem"}
                                        />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <h3 className="text-accent-foreground text-base font-medium">
                                            {`${slot.start_time} - ${slot.end_time}`}
                                          </h3>
                                          {templateType === "weekly" && slot.day_of_week && (
                                            <Badge className="bg-sidebar/30 text-accent-foreground">
                                              {getDayName(slot.day_of_week)}
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="mt-1 flex items-center gap-2">
                                          <p className="text-muted-foreground flex items-center gap-1 text-sm">
                                            <span className={"flex h-4 items-center gap-1 text-sm"}>
                                              <span className={"size-4"}>{icon}</span>
                                              {name}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center">
                                      {isSubmitting ? (
                                        <>
                                          {slot.status === "pending" && (
                                            <div className="bg-muted/70 flex h-8 w-8 items-center justify-center rounded-full">
                                              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                                            </div>
                                          )}
                                          {slot.status === "success" && (
                                            <motion.div
                                              animate={{ scale: 1, opacity: 1 }}
                                              className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20"
                                              initial={{ scale: 0.5, opacity: 0 }}
                                            >
                                              <Check className="h-5 w-5 text-green-500" />
                                            </motion.div>
                                          )}
                                          {slot.status === "error" && (
                                            <motion.div
                                              animate={{ scale: 1, opacity: 1 }}
                                              className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/20"
                                              initial={{ scale: 0.5, opacity: 0 }}
                                              title={slot.error}
                                            >
                                              <X className="h-5 w-5 text-red-500" />
                                            </motion.div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="bg-muted/70 flex h-8 w-8 items-center justify-center rounded-full">
                                          <Icon
                                            className="text-muted-foreground"
                                            height={"1.25rem"}
                                            icon="tabler:clock"
                                            width={"1.25rem"}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                                {slot.status === "error" && slot.error && (
                                  <motion.div
                                    key={`error-message-${slot.id}`}
                                    animate={{ opacity: 1, height: "auto" }}
                                    className="mt-2 px-4 pb-4"
                                    initial={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-500">
                                      <div className="flex items-start">
                                        <Icon
                                          className="mt-0.5 mr-2 flex-shrink-0"
                                          height={"1rem"}
                                          icon="tabler:alert-circle"
                                          width={"1rem"}
                                        />
                                        <div className="flex w-full items-center justify-between">
                                          <span>{slot.error}</span>
                                          {slot.errorType === "database_overlap" && (
                                            <InformationTooltip
                                              description={t(
                                                "forms:createSlotModal.errors.databaseOverlapTooltip",
                                              )}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </Card>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  </div>
                </ScrollArea>

                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="border-border bg-background flex h-16 items-center justify-between border-t px-6 py-1"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button disabled={isSubmitting} variant="outline" onClick={handleBackToCreation}>
                    <div className="flex items-center gap-1.5">
                      <Icon height="18" icon="tabler:arrow-left" width="18" />
                      <span>{t("common:back")}</span>
                    </div>
                  </Button>

                  <Button
                    disabled={isSubmitting || hasApiErrors()}
                    variant={"foreground"}
                    onClick={handleCreateSlots}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        {t("common:creating")}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Icon
                          height="18"
                          icon={
                            createMultiple || activeTab === "range"
                              ? "tabler:calendar-repeat"
                              : "tabler:plus"
                          }
                          width="18"
                        />
                        <span>{t("calendar:templates.confirmSlots")}</span>
                      </div>
                    )}
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
